import { FC } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { OfferResponseDTO, OfferPositionResponseDTO } from '../../../type/dto/RequestResponseDTO';
import { OfferStatus } from '../../RequestsCreatePage/type/enums/OfferStatus';

interface StatisticsTabProps {
    offers: OfferResponseDTO[];
}

export const StatisticsTab: FC<StatisticsTabProps> = ({ offers }) => {
    const acceptedOffer = offers?.find(
        (offer) =>
            offer.status === OfferStatus.ACCEPTED_BY_CLIENT ||
            offer.status === OfferStatus.ACCEPTED_BY_OPERATOR ||
            offer.status === OfferStatus.ACCEPTED_BY_OPERATOR_SUPERVISOR
    );

    if (!acceptedOffer) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="info">
                    No accepted offer yet. Statistics will be available after client accepts an offer.
                </Alert>
            </Box>
        );
    }

    const calculateTotals = () => {
        let totalPrice = 0;
        let totalItems = 0;

        acceptedOffer.offerPositions.forEach((position: OfferPositionResponseDTO) => {
            const itemTotal = position.price * position.quantity;
            totalPrice += itemTotal;
            totalItems += position.quantity;
        });

        return { totalPrice, totalItems };
    };

    const { totalPrice, totalItems } = calculateTotals();

    return (
        <Box>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: 3,
                            textAlign: 'center',
                            bgcolor: 'success.light',
                            color: 'success.contrastText',
                        }}
                    >
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            ${totalPrice.toFixed(2)}
                        </Typography>
                        <Typography variant="subtitle2">Total Amount</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: 3,
                            textAlign: 'center',
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                        }}
                    >
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            {totalItems}
                        </Typography>
                        <Typography variant="subtitle2">Total Items</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: 3,
                            textAlign: 'center',
                            bgcolor: 'info.light',
                            color: 'info.contrastText',
                        }}
                    >
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            {acceptedOffer.offerPositions.length}
                        </Typography>
                        <Typography variant="subtitle2">Product Lines</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Offer Details */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Accepted Offer
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Offer ID: {acceptedOffer.id}
                        </Typography>
                    </Box>
                    <Chip
                        label={acceptedOffer.status}
                        color="success"
                        sx={{ ml: 'auto' }}
                    />
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Positions Table */}
                <TableContainer>
                    <Table size="small" sx={{
                        '& .MuiTableCell-root': {
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                                borderRight: 'none',
                            },
                        },
                    }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.light' }}>
                                <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Product</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Brand</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                                    Price
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                                    Qty
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                                    Subtotal
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Delivery</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {acceptedOffer.offerPositions.map((position: OfferPositionResponseDTO, index: number) => {
                                const subtotal = position.price * position.quantity;
                                return (
                                    <TableRow key={position.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {position.productName}
                                            </Typography>
                                            {position.productCode && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Code: {position.productCode}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{position.brand || '-'}</TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                ${position.price.toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={position.quantity} size="small" color="primary" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                ${subtotal.toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {position.deliveryTime || 'Not specified'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {/* Total Row */}
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell colSpan={4} />
                                <TableCell align="center">
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Total:
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                                        ${totalPrice.toFixed(2)}
                                    </Typography>
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Additional Notes */}
            {acceptedOffer.offerPositions.some((p) => p.notes) && (
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Additional Notes
                    </Typography>
                    {acceptedOffer.offerPositions.map((position: OfferPositionResponseDTO, index: number) => {
                        if (!position.notes) return null;
                        return (
                            <Box key={position.id} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {index + 1}. {position.productName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {position.notes}
                                </Typography>
                            </Box>
                        );
                    })}
                </Paper>
            )}
        </Box>
    );
};

